/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Device-calendar mirror engine (expo-calendar).
 *
 * Mirrors the user's upcoming app events (next ~60 days) into ONE dedicated
 * calendar on the phone ("Happy Solutions") so they show up in the native
 * Calendar app — on iOS that includes the unified view beside Gmail/Google
 * accounts the user already sees there. The mirror is strictly one-way
 * (app → device): we never read or import foreign device events.
 *
 * Persistence (AsyncStorage):
 *  - HG_DEVICE_CAL_MIRROR_ENABLED — '1' when the user turned the mirror on.
 *  - HG_DEVICE_CAL_MIRROR_MAP — one JSON blob:
 *      { calendarId, events: { [appEventId]: { deviceEventId, signature } },
 *        lastSyncAt, eventCount }
 *
 * IMPORTANT: this module must stay leaf-level — it must NOT import
 * calendarApi (calendarApi imports it for post-mutation scheduleSync, which
 * would be a circular import). Callers hand events / event loaders in.
 */

import { Platform } from 'react-native';
import * as Calendar from 'expo-calendar';
import AsyncStorage from '@react-native-async-storage/async-storage';

/** AsyncStorage flag — mirror on/off (user setting). */
const MIRROR_ENABLED_KEY = 'HG_DEVICE_CAL_MIRROR_ENABLED';
/** AsyncStorage JSON blob — device calendar id + appEventId→deviceEventId map. */
const MIRROR_MAP_KEY = 'HG_DEVICE_CAL_MIRROR_MAP';

/** Title of the dedicated device calendar the mirror writes into. */
const CALENDAR_TITLE = 'Happy Solutions';
/** Brand orange — the calendar's color in the native Calendar app. */
const CALENDAR_COLOR = '#F97316';

/** How far ahead the mirror reaches (days). */
const MIRROR_WINDOW_DAYS = 60;
/** Debounce for {@link scheduleSync} bursts (ms). */
const SCHEDULE_DEBOUNCE_MS = 4000;

/** Pending debounced sync timer, or null when idle. */
let scheduleTimer = null;
/** The most recent event loader handed to {@link scheduleSync}. */
let scheduledLoader = null;

/**
 * Whether the device-calendar bridge exists on this platform at all
 * (native iOS/Android builds only — Expo web has no calendar store).
 *
 * @returns {boolean} true when expo-calendar can be used here
 */
export const isAvailable = () => Platform.OS === 'ios' || Platform.OS === 'android';

/**
 * Current calendar permission, without prompting.
 *
 * @returns {Promise<'granted'|'denied'|'undetermined'|'unavailable'>} status the
 *   settings screen can render directly
 */
export const getPermissionStatus = async () => {
    if (!isAvailable()) {
        return 'unavailable';
    }
    try {
        const { status } = await Calendar.getCalendarPermissionsAsync();

        return status || 'undetermined';
    } catch (_error) {
        return 'unavailable';
    }
};

/**
 * Prompt for calendar permission (no-op when already granted). Denied is a
 * normal, non-throwing outcome so the settings screen can show a friendly
 * state with an open-settings link.
 *
 * @returns {Promise<{status: string, granted: boolean, canAskAgain: boolean}>}
 */
export const requestPermission = async () => {
    if (!isAvailable()) {
        return { status: 'unavailable', granted: false, canAskAgain: false };
    }
    try {
        const result = await Calendar.requestCalendarPermissionsAsync();

        return {
            status: result?.status || 'denied',
            granted: result?.status === 'granted',
            canAskAgain: result?.canAskAgain !== false
        };
    } catch (_error) {
        return { status: 'unavailable', granted: false, canAskAgain: false };
    }
};

/**
 * Read the persisted mirror map blob (never throws).
 *
 * @returns {Promise<{calendarId: ?string, events: Object, lastSyncAt: ?string, eventCount: number}>}
 */
const readMirrorMap = async () => {
    try {
        const raw = await AsyncStorage.getItem(MIRROR_MAP_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        return {
            calendarId: parsed?.calendarId || null,
            events: parsed?.events && typeof parsed.events === 'object' ? parsed.events : {},
            lastSyncAt: parsed?.lastSyncAt || null,
            eventCount: Number(parsed?.eventCount) || 0
        };
    } catch (_error) {
        return { calendarId: null, events: {}, lastSyncAt: null, eventCount: 0 };
    }
};

/**
 * Persist the mirror map blob (best-effort, never throws).
 *
 * @param {Object} map - blob in the {@link readMirrorMap} shape
 * @returns {Promise<void>}
 */
const writeMirrorMap = async (map) => {
    try {
        await AsyncStorage.setItem(MIRROR_MAP_KEY, JSON.stringify(map));
    } catch (_error) {
        /* Storage full/unavailable — next sweep rebuilds from the device side. */
    }
};

/**
 * Whether the user has the mirror switched on.
 *
 * @returns {Promise<boolean>}
 */
export const isMirrorEnabled = async () => {
    try {
        return (await AsyncStorage.getItem(MIRROR_ENABLED_KEY)) === '1';
    } catch (_error) {
        return false;
    }
};

/**
 * Pick the iOS source the dedicated calendar should live under: the default
 * calendar's own source when resolvable, else the iCloud CalDAV source, else
 * the local source (expo-calendar docs pattern).
 *
 * @returns {Promise<?string>} a source id, or null when none is usable
 */
const pickIosSourceId = async () => {
    try {
        const defaultCalendar = await Calendar.getDefaultCalendarAsync();

        if (defaultCalendar?.source?.id) {
            return defaultCalendar.source.id;
        }
    } catch (_error) {
        /* No default calendar configured — fall through to the source list. */
    }
    try {
        const sources = await Calendar.getSourcesAsync();
        const icloud = sources.find((s) => s.type === Calendar.SourceType.CALDAV && /icloud/i.test(s.name || ''));
        const local = sources.find((s) => s.type === Calendar.SourceType.LOCAL);

        return (icloud || local || sources[0])?.id || null;
    } catch (_error) {
        return null;
    }
};

/**
 * Find-or-create the dedicated "Happy Solutions" device calendar and return
 * its id. Reuses the persisted id when the calendar still exists, then falls
 * back to a title match, then creates it (iOS under a sensible source, Android
 * as a local calendar). Requires granted permission.
 *
 * @returns {Promise<?string>} the device calendar id, or null on any failure
 */
export const ensureAppCalendar = async () => {
    if ((await getPermissionStatus()) !== 'granted') {
        return null;
    }
    try {
        const map = await readMirrorMap();
        const calendars = await Calendar.getCalendarsAsync(Calendar.EntityTypes.EVENT);

        if (map.calendarId && calendars.some((c) => c.id === map.calendarId)) {
            return map.calendarId;
        }

        const existing = calendars.find((c) => c.title === CALENDAR_TITLE && c.allowsModifications !== false);
        let calendarId = existing?.id || null;

        if (!calendarId) {
            const details = {
                title: CALENDAR_TITLE,
                name: CALENDAR_TITLE,
                color: CALENDAR_COLOR,
                entityType: Calendar.EntityTypes.EVENT,
                ownerAccount: CALENDAR_TITLE,
                accessLevel: Calendar.CalendarAccessLevel.OWNER
            };

            if (Platform.OS === 'ios') {
                const sourceId = await pickIosSourceId();

                if (!sourceId) {
                    return null;
                }
                details.sourceId = sourceId;
            } else {
                details.source = { isLocalAccount: true, name: CALENDAR_TITLE };
            }
            calendarId = await Calendar.createCalendarAsync(details);
        }

        if (calendarId && calendarId !== map.calendarId) {
            await writeMirrorMap({ ...map, calendarId });
        }

        return calendarId || null;
    } catch (_error) {
        return null;
    }
};

/**
 * Normalize one app event into the expo-calendar event shape. Per-event
 * reminders (style 'notification' AND 'alarm' alike — the native calendar has
 * one alert concept) become absolute-offset alarms so the PHONE also alerts,
 * not just the app. Falls back to the legacy scalar `reminderTime` when the
 * new `reminders` array is absent.
 *
 * @param {Object} event - app event ({ id|_id, title, start, end, location, allDay, notifications })
 * @returns {?{id: string, details: Object, signature: string}} null when the event is unusable
 */
const normalizeEvent = (event) => {
    const id = String(event?.id || event?._id || '');
    const start = event?.start ? new Date(event.start) : null;

    if (!id || !start || Number.isNaN(start.getTime())) {
        return null;
    }
    const end = event?.end && !Number.isNaN(new Date(event.end).getTime())
        ? new Date(event.end)
        : new Date(start.getTime() + 60 * 60 * 1000);

    let alarms = [];

    if (event?.notifications && event.notifications.enabled !== false) {
        const reminders = Array.isArray(event.notifications.reminders) ? event.notifications.reminders : [];

        alarms = reminders
            .filter((r) => Number.isFinite(Number(r?.minutesBefore)))
            .map((r) => ({ relativeOffset: -Math.abs(Number(r.minutesBefore)) }));
        if (alarms.length === 0 && Number.isFinite(Number(event.notifications.reminderTime))) {
            alarms = [{ relativeOffset: -Math.abs(Number(event.notifications.reminderTime)) }];
        }
    }

    let timeZone;

    try {
        timeZone = globalThis.Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (_error) {
        /* Older JS engines — expo-calendar tolerates a missing timeZone. */
    }

    const details = {
        title: event?.title || '',
        startDate: start,
        endDate: end,
        location: event?.location || '',
        allDay: Boolean(event?.allDay),
        alarms,
        timeZone
    };
    // Change detection without re-reading the device store: a stable signature
    // over every field the mirror writes.
    const signature = JSON.stringify([
        details.title,
        start.toISOString(),
        end.toISOString(),
        details.location,
        details.allDay,
        alarms.map((a) => a.relativeOffset)
    ]);

    return { id, details, signature };
};

/**
 * One full mirror sweep: upsert the given app events (clamped to the next
 * ~{@link MIRROR_WINDOW_DAYS} days) into the dedicated device calendar —
 * create missing, update changed (title/start/end/location/allDay/alarms),
 * delete device events whose app event vanished from the fed set. Every
 * device call is individually guarded: one bad event never aborts the sweep.
 *
 * @param {Array<Object>} events - the user's upcoming app events (from calendarApi.fetchMyEvents)
 * @returns {Promise<{ok: boolean, reason?: string, created?: number, updated?: number, deleted?: number, errors?: number, eventCount?: number}>}
 */
export const mirrorEvents = async (events) => {
    if ((await getPermissionStatus()) !== 'granted') {
        return { ok: false, reason: 'permission' };
    }
    const calendarId = await ensureAppCalendar();

    if (!calendarId) {
        return { ok: false, reason: 'calendar' };
    }

    const map = await readMirrorMap();
    const nextEvents = {};
    const now = Date.now();
    const horizon = now + MIRROR_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    let created = 0;
    let updated = 0;
    let deleted = 0;
    let errors = 0;

    const list = Array.isArray(events) ? events : [];

    for (const raw of list) {
        const normalized = normalizeEvent(raw);

        if (!normalized) {
            continue;
        }
        const startMs = normalized.details.startDate.getTime();
        const endMs = normalized.details.endDate.getTime();

        // Only mirror the upcoming window; still-running events stay too.
        if (endMs < now || startMs > horizon) {
            continue;
        }
        const known = map.events[normalized.id];

        try {
            if (known?.deviceEventId) {
                if (known.signature !== normalized.signature) {
                    // eslint-disable-next-line no-await-in-loop
                    await Calendar.updateEventAsync(known.deviceEventId, normalized.details);
                    updated += 1;
                }
                nextEvents[normalized.id] = { deviceEventId: known.deviceEventId, signature: normalized.signature };
            } else {
                // eslint-disable-next-line no-await-in-loop
                const deviceEventId = await Calendar.createEventAsync(calendarId, normalized.details);

                nextEvents[normalized.id] = { deviceEventId, signature: normalized.signature };
                created += 1;
            }
        } catch (_error) {
            errors += 1;
            if (known?.deviceEventId) {
                // The device event may have been deleted by hand — recreate once.
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const deviceEventId = await Calendar.createEventAsync(calendarId, normalized.details);

                    nextEvents[normalized.id] = { deviceEventId, signature: normalized.signature };
                    created += 1;
                } catch (_recreateError) {
                    /* Give up on this one event; the sweep continues. */
                }
            }
        }
    }

    // Delete device events whose app event vanished (or left the window).
    for (const [appEventId, entry] of Object.entries(map.events)) {
        if (!nextEvents[appEventId] && entry?.deviceEventId) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await Calendar.deleteEventAsync(entry.deviceEventId);
                deleted += 1;
            } catch (_error) {
                errors += 1;
            }
        }
    }

    const eventCount = Object.keys(nextEvents).length;

    await writeMirrorMap({
        calendarId,
        events: nextEvents,
        lastSyncAt: new Date().toISOString(),
        eventCount
    });

    return { ok: true, created, updated, deleted, errors, eventCount };
};

/**
 * Turn the mirror on or off. Disabling removes every mapped device event (the
 * dedicated calendar itself is kept, so re-enabling is instant) and clears the
 * appEventId→deviceEventId map.
 *
 * @param {boolean} enabled - the new state of the user's mirror switch
 * @returns {Promise<void>}
 */
export const setMirrorEnabled = async (enabled) => {
    try {
        await AsyncStorage.setItem(MIRROR_ENABLED_KEY, enabled ? '1' : '0');
    } catch (_error) {
        /* Flag write failure — treated as still-disabled on next read. */
    }
    if (enabled) {
        return;
    }
    if (scheduleTimer) {
        globalThis.clearTimeout(scheduleTimer);
        scheduleTimer = null;
        scheduledLoader = null;
    }
    const map = await readMirrorMap();

    for (const entry of Object.values(map.events)) {
        if (entry?.deviceEventId) {
            try {
                // eslint-disable-next-line no-await-in-loop
                await Calendar.deleteEventAsync(entry.deviceEventId);
            } catch (_error) {
                /* Already gone or store unavailable — clearing the map suffices. */
            }
        }
    }
    await writeMirrorMap({
        ...map,
        events: {},
        lastSyncAt: new Date().toISOString(),
        eventCount: 0
    });
};

/**
 * Mirror status snapshot for the settings screen.
 *
 * @returns {Promise<{available: boolean, enabled: boolean, permission: string, lastSyncAt: ?string, eventCount: number}>}
 */
export const getMirrorStatus = async () => {
    const [enabled, permission, map] = await Promise.all([
        isMirrorEnabled(),
        getPermissionStatus(),
        readMirrorMap()
    ]);

    return {
        available: isAvailable(),
        enabled,
        permission,
        lastSyncAt: map.lastSyncAt,
        eventCount: map.eventCount
    };
};

/**
 * Debounced fire-and-forget full mirror pass. Callers (calendarApi after a
 * successful create/update/delete) hand in an async event LOADER — not the
 * events themselves — so the pass that eventually runs fetches a fresh window.
 * No-op unless the mirror is enabled and permission is granted; never throws
 * and never blocks the caller.
 *
 * @param {function(): Promise<Array<Object>>} loadEvents - resolves the user's upcoming events
 * @returns {void}
 */
export const scheduleSync = (loadEvents) => {
    if (typeof loadEvents === 'function') {
        scheduledLoader = loadEvents;
    }
    if (!scheduledLoader || !isAvailable()) {
        return;
    }
    if (scheduleTimer) {
        globalThis.clearTimeout(scheduleTimer);
    }
    scheduleTimer = globalThis.setTimeout(async () => {
        scheduleTimer = null;
        const loader = scheduledLoader;

        try {
            if (!(await isMirrorEnabled()) || (await getPermissionStatus()) !== 'granted') {
                return;
            }
            const events = await loader();

            await mirrorEvents(events);
        } catch (_error) {
            /* Best-effort mirror — the next mutation or manual sync retries. */
        }
    }, SCHEDULE_DEBOUNCE_MS);
};

export default {
    isAvailable,
    getPermissionStatus,
    requestPermission,
    ensureAppCalendar,
    mirrorEvents,
    setMirrorEnabled,
    isMirrorEnabled,
    getMirrorStatus,
    scheduleSync
};
